import { CKEditor4 } from 'ckeditor4-angular';
import { CommonVerbsApiService } from '@services/common/common-verbs-api.service';
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Newsletters } from '@models/communities/newsletters.model';
import { ToastrService } from 'ngx-toastr';

@Injectable()
export class NewsletterService {
  INLINE: CKEditor4.EditorType = CKEditor4.EditorType.INLINE;
  PARAGRAPH = {
    delayIfDetached: true,
    extraPlugins: 'wordcount, justify, font, placeholder,selectall',
    fontSize_sizes:
      '8px/10.67px;10px/13.34px;12px/16px;14px/18.67px;16px/21.34px',
    fontSize_defaultLabel: '10px',
    toolbarGroups: [
      { name: 'clipboard', groups: ['undo'] },
      {
        groups: ['find', 'selection', 'spellchecker', 'editing'],
      },
      {
        name: 'basicstyles',
        groups: ['basicstyles', 'cleanup', 'FontSize', 'SelectAll'],
      },
      '/',
      { name: 'styles', groups: ['Styles'] },
      {
        name: 'paragraph',
        groups: ['list', 'indent', 'blocks', 'align', 'bidi', 'paragraph'],
      },
    ],
    removeButtons:
      'PasteFromWord,PasteText,Paste,Styles,Blockquote,Format,NumberedList,Font',
    wordcount: {
      showParagraphs: false,
      showWordCount: false,
      showCharCount: true,
      countSpacesAsChars: true,
      countHTML: false,
      maxWordCount: 100,
      maxCharCount: 600,
    },
  };

  constructor(
    private api: CommonVerbsApiService,
    private toastr: ToastrService
  ) {}

  limitContent(limit: number) {
    return {
      ...this.PARAGRAPH,
      wordcount: {
        ...this.PARAGRAPH.wordcount,
        maxCharCount: limit,
      },
    };
  }

  onNamespaceLoaded(CKEDITOR: any) {
    CKEDITOR.plugins.addExternal(
      'wordcount',
      '/assets/js/ckeditor/plugins/wordcount/',
      'plugin2025.js?timestamp=' + new Date().getTime()
    );
    CKEDITOR.plugins.addExternal(
      'font',
      '/assets/js/ckeditor/plugins/font/',
      'plugin.js?timestamp=' + new Date().getTime()
    );
    CKEDITOR.on('instanceReady', function (event: any) {
      event.editor.on('beforeCommandExec', function (event: any) {
        // Show the paste dialog for the paste buttons and right-click paste
        if (event.data.name == 'paste') {
          event.editor._.forcePasteDialog = true;
        }
        // Don't show the paste dialog for Ctrl+Shift+V
        if (
          event.data.name == 'pastetext' &&
          event.data.commandData.from == 'keystrokeHandler'
        ) {
          event.cancel();
        }
      });
    });
  }

  update(
    newsletter: Newsletters | null,
    jsonContent: any
  ): Promise<Newsletters | null> {
    return new Promise((resolve, reject) => {
      const postData = {
        jsonContent: jsonContent,
      };
      this.api
        .put<Newsletters>(`communities/newsletters/${newsletter?.id}`, postData)
        .subscribe(
          (newsletter: Newsletters) => {
            resolve(newsletter);
          },
          (errorEventAdded: HttpErrorResponse) => {
            const errorFields = Object.keys(errorEventAdded?.error.errors);
            if (errorFields.length > 0) {
              errorFields.forEach((field: string) => {
                this.toastr.error(
                  errorEventAdded?.error.errors[field] || 'An error occurred.'
                );
              });
            }
            reject(errorEventAdded);
          }
        );
    });
  }

  lightOrDark(color: any) {
    // Variables for red, green, blue values
    let r, g, b;
    let hsp = 0;

    // Check the format of the color, HEX or RGB?
    if (color.match(/^rgb/)) {
      // If RGB --> store the red, green, blue values in separate variables
      color = color.match(
        /^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d+(?:\.\d+)?))?\)$/
      );
      r = color[1];
      g = color[2];
      b = color[3];
    } else {
      // If hex --> Convert it to RGB: http://gist.github.com/983661
      color = +(
        '0x' + color.slice(1).replace(color.length < 5 && /./g, '$&$&')
      );
      r = color >> 16;
      g = (color >> 8) & 255;
      b = color & 255;
    }

    // HSP (Highly Sensitive Poo) equation from http://alienryderflex.com/hsp.html
    hsp = Math.sqrt(0.299 * (r * r) + 0.587 * (g * g) + 0.114 * (b * b));

    // Using the HSP value, determine whether the color is light or dark
    if (hsp > 127.5) {
      return 'light';
    } else {
      return 'dark';
    }
  }
}
